import React, { useState, useEffect } from 'react';
import { Brain, Eye, EyeOff } from 'lucide-react';
import { useAITokens } from '../../hooks/useAITokens';
import LoadingSpinner from '../ui/LoadingSpinner';
import { cn } from '../../utils/cn';

interface TokenField {
  id: string;
  name: string;
  label: string;
}

const TOKEN_FIELDS: TokenField[] = [
  { id: 'openai_token', name: 'openai_token', label: 'OpenAI Token' },
  { id: 'gemini_token', name: 'gemini_token', label: 'Gemini Token' },
  { id: 'perplexity_token', name: 'perplexity_token', label: 'Perplexity Token' },
  { id: 'grok_token', name: 'grok_token', label: 'Grok Token' },
  { id: 'mistral_token', name: 'mistral_token', label: 'Mistral Token' }
];

export default function AISettings() {
  const { tokens, loading, error, updateTokens } = useAITokens();
  const [revealedTokens, setRevealedTokens] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [formTokens, setFormTokens] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (tokens) {
      setFormTokens(tokens);
    }
  }, [tokens]);

  const toggleReveal = (tokenId: string) => {
    setRevealedTokens(prev => {
      const next = new Set(prev);
      if (next.has(tokenId)) {
        next.delete(tokenId);
      } else {
        next.add(tokenId);
      }
      return next;
    });
  };

  const handleInputChange = (tokenId: string, value: string) => {
    setFormTokens(prev => ({
      ...prev,
      [tokenId]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateTokens(formTokens);
      setHasChanges(false);
    } catch (err) {
      // Error is handled by the hook
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading AI settings..." />;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <div className="card p-6">
      <div className="flex items-center gap-2 mb-6">
        <Brain className="w-5 h-5 text-primary-500" />
        <div className="flex-1 flex justify-between items-center">
          <h2 className="text-lg font-semibold">AI Integration</h2>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={cn(
              "btn btn-primary flex items-center gap-2",
              !hasChanges && "opacity-50 cursor-not-allowed"
            )}
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {TOKEN_FIELDS.map(field => (
          <div key={field.id}>
            <label htmlFor={field.id} className="block text-sm font-medium text-gray-300 mb-2">
              {field.label}
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type={revealedTokens.has(field.id) ? 'text' : 'password'}
                  id={field.id}
                  className="input w-full pr-24"
                  value={formTokens[field.id] || ''}
                  onChange={(e) => handleInputChange(field.id, e.target.value)}
                  placeholder={`Enter your ${field.label}`}
                />
                <button
                  type="button"
                  onClick={() => toggleReveal(field.id)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-2"
                >
                  {revealedTokens.has(field.id) ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}

        <div className="text-sm text-gray-400 mt-4">
          <p>These tokens will be used to interact with various AI services for enhanced functionality.</p>
        </div>
      </div>
    </div>
  );
}